import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { ACTION_TYPES } from '../../api/firebase/actionReturn';
import { dataTestIds } from '../../config/data-testids';
import {
  getNovaAccountsUrl,
  getSelfServeOnboardingUrl,
} from '../../config/env';
import { useAuthState } from '../../providers/authState';
import { useCookie } from '../../providers/cookies';
import { useTranslation } from '../../providers/i18next';
import { useReturnValue } from '../../providers/returnValue';
import { logError } from '../../providers/rollbar';
import { RETURN_ROUTE } from '../../routes/constants';
import { ExternalLink } from '../Links';
import {
  BaseSigninForm,
  FormHeading,
  FormSubtitle,
  LegalFooter,
} from './Parts';

const LOGIN_SOURCE_COOKIE = 'login_source';

/**
 * Appends returnValue.ret as a redirect_to query param to the given URL.
 * Returns the URL unchanged when no ret value is present.
 */
const appendRedirectTo = (url, ret) => {
  if (!ret) return url;
  return `${url}?redirect_to=${encodeURIComponent(ret)}`;
};

const SigninForm = ({ resetMode = false } = {}) => {
  const { txn } = useTranslation();
  const didRedirectRef = useRef(false);
  const { isNoSignUpMode, replaceReturnValue, returnValue } = useReturnValue();
  const navigate = useNavigate();
  const { authUser, consumeActionReturn, isReady, isLoading, isUserLoading } =
    useAuthState();
  const userId = authUser?.uid;
  const hasUser = !!authUser && !!userId;
  const [loginSource] = useCookie(LOGIN_SOURCE_COOKIE);
  const { isLoginSourceRedirectEnabled, isNovaRedirectEnabled } = useFlags();

  const showSignup = !isNoSignUpMode && !resetMode;
  const selfServeOnboardingUrl = getSelfServeOnboardingUrl();

  // Feature flag: isLoginSourceRedirectEnabled
  //
  // When enabled, redirects logged out users who originally logged in with
  // Nova back to the Nova app's login page instead of showing the
  // email/password form. This handles the SSO race condition where CCMS
  // auto-signs users out and they land here unexpectedly.
  //
  // Waits for auth initialization to fully resolve before deciding.
  // This prevents a redirect loop in the normal CCMS→Accounts auth
  // flow: during the auth loading window the user appears
  // unauthenticated, but once auth resolves GuestRoute will redirect
  // authenticated users to /return. We only redirect genuinely
  // logged-out users.
  //
  // Intentionally does NOT check returnValue.ret, because the SSO
  // race condition sends users here WITH ret data (from CCMS) but
  // if the user is genuinely logged out and needs to re-authenticate through
  // their original login source (e.g., Nova).
  //
  // If they are already logged in then this new code can be ignored, and they
  // go back to the CCMS app via returnValue.ret like before.
  let shouldRedirectToLoginSource = false;
  if (isLoginSourceRedirectEnabled) {
    const isAuthResolved = isReady && !isLoading && !isUserLoading;
    shouldRedirectToLoginSource =
      !resetMode && !!loginSource && isAuthResolved && !hasUser;
  }

  useEffect(() => {
    if (shouldRedirectToLoginSource) {
      window.location.replace(appendRedirectTo(loginSource, returnValue?.ret));
    }
  }, [shouldRedirectToLoginSource, loginSource, returnValue]);

  const onSigninComplete = () => {
    // reset mode handled in useEffect
    if (!resetMode && !didRedirectRef.current) {
      didRedirectRef.current = false;
      navigate(RETURN_ROUTE, { replace: true });
    }
  };

  useEffect(() => {
    async function doAsync() {
      if (hasUser && resetMode && !didRedirectRef.current) {
        didRedirectRef.current = true;
        let actionReturn;
        try {
          actionReturn = await consumeActionReturn(
            userId,
            ACTION_TYPES.RESET_PASSWORD
          );
          if (actionReturn) {
            replaceReturnValue(actionReturn);
          }
        } catch (err) {
          logError(
            `Could not consume action return during signin: ${err.message}`,
            err,
            {
              err,
              resetMode,
              actionReturn,
              userId,
              type: ACTION_TYPES.RESET_PASSWORD,
            }
          );
        }
        navigate(RETURN_ROUTE, { replace: true });
      }
    }
    doAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUser]);

  // Feature flag: isNovaRedirectEnabled
  //
  // When enabled, redirects all unauthenticated users to the Nova
  // /accounts page instead of showing the email/password form. Nova's
  // /accounts page provides an alternative login UI. This is a catch-all
  // that fires after the more specific login_source cookie redirect above.
  //
  // Passes through returnValue.ret as the redirect_to query param so
  // that after authenticating in Nova, the user ends up where they
  // originally intended to go (e.g., CCMS). When no ret is set
  // (direct navigation), redirects to Nova without the param.
  let shouldRedirectToNova = false;
  if (isNovaRedirectEnabled) {
    const isAuthResolved = isReady && !isLoading && !isUserLoading;
    shouldRedirectToNova =
      !resetMode && !shouldRedirectToLoginSource && isAuthResolved && !hasUser;
  }

  useEffect(() => {
    if (shouldRedirectToNova) {
      window.location.replace(
        appendRedirectTo(getNovaAccountsUrl(), returnValue?.ret)
      );
    }
  }, [shouldRedirectToNova, returnValue]);

  // Don't flash the form while redirecting
  if (shouldRedirectToLoginSource || shouldRedirectToNova) {
    return null;
  }

  return (
    <>
      <FormHeading>
        {txn(resetMode ? 'Finish signing in' : 'Welcome back')}
      </FormHeading>
      {showSignup && (
        <FormSubtitle>
          {txn('First time here?')}{' '}
          <ExternalLink
            useNewTab={false}
            href={selfServeOnboardingUrl}
            data-testid={dataTestIds.signinForm.signUpLink}
          >
            {txn('Sign up')}
          </ExternalLink>
        </FormSubtitle>
      )}
      <BaseSigninForm
        onSigninComplete={onSigninComplete}
        showForgotPasswordLink={!resetMode}
        data-testid={dataTestIds.signinForm.baseSigninForm}
      />
      {!resetMode && <LegalFooter />}
    </>
  );
};

export default SigninForm;
